import React, { useState } from 'react';
import { X, Upload, FileText, Loader2, CheckCircle, Info, Layers, List, FileCheck, Plus, Trash2 } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { formatRepertoriosRange } from '../utils/formatters.js';

export default function FinalizarTramiteModal({ tramite, onClose, onUpdate }) {
  const [modo, setModo] = useState('global'); // 'global' | 'individual'
  const [globalFiles, setGlobalFiles] = useState([]);
  const [files, setFiles] = useState({}); // { [repertorioId]: File }
  const [loading, setLoading] = useState(false);

  const activeRepertorios = (tramite.repertorios || []).filter(r => !r.is_vencido);

  const handleIndividualFileChange = (repertorioId, file) => {
    if (file) {
      setFiles(prev => ({ ...prev, [repertorioId]: file }));
    }
  };

  const handleAddGlobalFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const newFiles = Array.from(fileList);
    setGlobalFiles(prev => {
      const existingKeys = new Set(prev.map(f => `${f.name}-${f.size}`));
      const uniqueNew = newFiles.filter(f => !existingKeys.has(`${f.name}-${f.size}`));
      return [...prev, ...uniqueNew];
    });
  };

  const handleRemoveGlobalFile = (indexToRemove) => {
    setGlobalFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (modo === 'global') {
      if (globalFiles.length === 0) {
        toast.error('Debe adjuntar al menos un documento global.');
        return;
      }
    } else {
      const repertorioIds = activeRepertorios.map(r => r.id);
      const missing = repertorioIds.filter(id => !files[id]);

      if (missing.length > 0) {
        const result = await Swal.fire({
          title: 'Documentos Faltantes',
          text: `Hay ${missing.length} repertorio(s) sin documento individual asignado. ¿Desea finalizar de todas formas?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: 'var(--primary)',
          cancelButtonColor: '#64748b',
          confirmButtonText: 'Sí, finalizar',
          cancelButtonText: 'Cancelar'
        });
        if (!result.isConfirmed) return;
      }

      if (Object.keys(files).length === 0) {
        toast.error('Debe adjuntar al menos un documento.');
        return;
      }
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('modo', modo);

    if (modo === 'global') {
      globalFiles.forEach(file => {
        formData.append('documentos[]', file);
      });
    } else {
      const mapping = {};
      Object.entries(files).forEach(([id, file]) => {
        formData.append('documentos[]', file);
        mapping[file.name] = id;
      });
      formData.append('mapping', JSON.stringify(mapping));
    }

    try {
      await api.post(`/tramites/${tramite.id}/finalizar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Trámite finalizado y documentos enviados exitosamente.');
      if (onUpdate) onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al finalizar el trámite.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px', width: '95%', padding: 0, borderRadius: '12px', overflow: 'hidden' }}>
        
        {/* Encabezado */}
        <div className="modal-header" style={{ padding: '1.2rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ padding: '8px', background: '#ecfdf5', color: '#059669', borderRadius: '8px', display: 'flex' }}>
              <CheckCircle size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-color, #0f172a)' }}>Finalizar Trámite</h3>
              <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Carga de documentos oficiales y notificación por correo</span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '1.5rem' }}>
            
            {/* Tarjeta de Información del Trámite para el Funcionario */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                <span style={{ fontWeight: 800, fontSize: '1rem', color: '#003366', fontFamily: 'monospace' }}>
                  #{tramite.numero_seguimiento}
                </span>
                <span style={{ fontSize: '0.75rem', background: '#e0f2fe', color: '#0369a1', padding: '3px 8px', borderRadius: '6px', fontWeight: 700 }}>
                  {tramite.tipo_inscripcion?.nombre || tramite.tipoInscripcion?.nombre || 'Trámite'} ({tramite.area?.nombre || 'General'})
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', fontSize: '0.85rem' }}>
                <div>
                  <span style={{ color: '#64748b' }}>Solicitante: </span>
                  <strong style={{ color: '#1e293b' }}>{tramite.nombre_solicitante}</strong>
                  {tramite.rut_solicitante && <span style={{ color: '#64748b', fontSize: '0.78rem' }}> ({tramite.rut_solicitante})</span>}
                </div>
                <div>
                  <span style={{ color: '#64748b' }}>Repertorio(s): </span>
                  <strong style={{ color: 'var(--copper, #b45309)' }}>
                    📚 {formatRepertoriosRange(tramite.repertorios) || 'Sin asignar'}
                  </strong>
                </div>
              </div>

              {tramite.correo && (
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '6px' }}>
                  Correo de entrega: <strong style={{ color: '#0284c7' }}>{tramite.correo}</strong>
                </div>
              )}
            </div>

            {/* Selector de Modo: Global vs Individual */}
            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '8px', color: '#334155' }}>
                Modalidad de Carga de Documentos:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
                <button
                  type="button"
                  onClick={() => setModo('global')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    borderRadius: '6px',
                    border: 'none',
                    background: modo === 'global' ? '#ffffff' : 'transparent',
                    color: modo === 'global' ? '#0f172a' : '#64748b',
                    boxShadow: modo === 'global' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <Layers size={16} color={modo === 'global' ? '#059669' : '#64748b'} />
                  Documentos Globales (Trámite Completo)
                </button>
                <button
                  type="button"
                  onClick={() => setModo('individual')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    borderRadius: '6px',
                    border: 'none',
                    background: modo === 'individual' ? '#ffffff' : 'transparent',
                    color: modo === 'individual' ? '#0f172a' : '#64748b',
                    boxShadow: modo === 'individual' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <List size={16} color={modo === 'individual' ? '#059669' : '#64748b'} />
                  Por Repertorio Individual ({activeRepertorios.length})
                </button>
              </div>
            </div>

            {/* Formulario según Modo */}
            {modo === 'global' ? (
              <div>
                {globalFiles.length === 0 ? (
                  <div style={{ background: '#f8fafc', border: '2px dashed #cbd5e1', borderRadius: '10px', padding: '1.8rem', textAlign: 'center', position: 'relative' }}>
                    <Upload size={30} color="#64748b" style={{ marginBottom: '8px' }} />
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b', marginBottom: '4px' }}>
                      Subir documentos finales del trámite
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '14px' }}>
                      Puedes seleccionar uno o varios archivos PDF/imágenes. Se asociarán al trámite y cubrirán todos sus repertorios. (Max 10MB c/u)
                    </div>
                    <input 
                      type="file" 
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        handleAddGlobalFiles(e.target.files);
                        e.target.value = '';
                      }}
                      style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                    />
                    <button type="button" className="btn btn-outline" style={{ pointerEvents: 'none', fontSize: '0.85rem' }}>
                      Seleccionar Archivo(s)
                    </button>
                  </div>
                ) : (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FileCheck size={18} color="#059669" />
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>
                          Documentos seleccionados ({globalFiles.length})
                        </span>
                      </div>
                      <label style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '5px', 
                        fontSize: '0.78rem', 
                        fontWeight: 600, 
                        color: '#0284c7', 
                        background: '#e0f2fe', 
                        padding: '5px 10px', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        transition: 'background 0.15s ease'
                      }}>
                        <Plus size={14} />
                        Agregar más archivos
                        <input 
                          type="file" 
                          multiple 
                          accept=".pdf,.jpg,.jpeg,.png" 
                          onChange={(e) => {
                            handleAddGlobalFiles(e.target.files);
                            e.target.value = '';
                          }}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto', paddingRight: '4px' }}>
                      {globalFiles.map((file, idx) => (
                        <div 
                          key={`${file.name}-${idx}`} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            background: '#ffffff', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '8px', 
                            padding: '8px 12px' 
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: '#dcfce7', color: '#166534', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <FileText size={18} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '380px' }}>
                                {file.name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {(file.size / 1024 / 1024).toFixed(2)} MB • Listo para enviar
                              </div>
                            </div>
                          </div>

                          <button 
                            type="button" 
                            onClick={() => handleRemoveGlobalFile(idx)}
                            title="Eliminar este archivo"
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              color: '#94a3b8', 
                              cursor: 'pointer', 
                              padding: '4px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '280px', overflowY: 'auto', paddingRight: '4px' }}>
                {activeRepertorios.length > 0 ? (
                  activeRepertorios.map(rep => (
                    <div key={rep.id} style={{ 
                      padding: '10px 14px', 
                      background: files[rep.id] ? '#f0fdf4' : '#f8fafc', 
                      border: files[rep.id] ? '1px solid #86efac' : '1px solid #e2e8f0', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                          Repertorio #{rep.numero_repertorio}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {rep.fecha_repertorio ? new Date(rep.fecha_repertorio).toLocaleDateString('es-CL') : 'Sin fecha'}
                          {rep.nombre_requirente && ` • ${rep.nombre_requirente}`}
                        </div>
                      </div>

                      <div style={{ 
                        position: 'relative', 
                        border: '1px dashed #cbd5e1', 
                        borderRadius: '6px', 
                        padding: '6px 12px', 
                        background: files[rep.id] ? '#ffffff' : '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        maxWidth: '220px'
                      }}>
                        <Upload size={14} className={files[rep.id] ? 'text-success' : 'text-muted'} />
                        <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: files[rep.id] ? '#15803d' : '#64748b', fontWeight: files[rep.id] ? 600 : 400 }}>
                          {files[rep.id] ? files[rep.id].name : 'Subir documento...'}
                        </span>
                        <input 
                          type="file" 
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleIndividualFileChange(rep.id, e.target.files[0])}
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
                    Este trámite no tiene repertorios activos. Puedes usar el modo global para adjuntar el documento.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer" style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading} style={{ padding: '0.55rem 1.2rem', borderRadius: '8px' }}>
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn-success" 
              style={{ background: '#059669', color: 'white', border: 'none', padding: '0.55rem 1.5rem', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}
              disabled={loading}
            >
              {loading ? <><Loader2 className="animate-spin" size={16} /> Procesando...</> : 'Finalizar y Enviar al Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
